import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { getRuntimeConfig } from '@/shared/runtimeConfig';
import { envColor } from '@/shared/common/envColors';
import { Button } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import { Input } from '@/shared/ui/input';
import { LanguageSwitcher } from '@/shared/ui/language';
import { useAuthActions } from './AuthContext';

/** Serverový `detail` (lokalizuje backend) má přednost; jinak lokalizovaný fallback. */
function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
    if (detail) return detail;
  }
  return fallback;
}

/** Badge prostředí (PROD / TEST / DEV) v patičce přihlašovací karty. Barvy viz `@/shared/common/envColors`. */
function EnvBadge({ env }: { env: string }) {
  const colors = envColor(env);
  return (
    <span
      className="rounded-phx-sm text-phx-2xs tracking-phx-wide inline-flex h-[18px] items-center px-1.5 font-bold uppercase"
      style={{ background: colors.bg, color: colors.fg }}
    >
      {env}
    </span>
  );
}

// Jemný mřížkový raster „pracovní plochy" pod kartou (z designu .lg-grid).
const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(140,126,95,0.06) 1px, transparent 1px),' +
    'linear-gradient(90deg, rgba(140,126,95,0.06) 1px, transparent 1px)',
  backgroundSize: '30px 30px',
} as const;

/**
 * Celoobrazovková přihlašovací brána pro Phoenix-On-Top režim (Phoenix = autorita přihlášení).
 * Zobrazí se, když není platná Phoenix session. Po úspěšném přihlášení AuthProvider přepne
 * stav na `authenticated` a tato komponenta se odmonteruje.
 *
 * Vzhled: centrovaná karta (varianta A designového handoffu „Login dialog") na warm pergamen
 * ploše, brick/akcent z aktuálního Phoenix tématu. Verze + prostředí jdou z `/api/config/runtime`.
 */
export function LoginGate() {
  const { t } = useTranslation('auth');
  const { login } = useAuthActions();
  const { version, environment } = getRuntimeConfig().app;
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      await login(userName, password);
      // úspěch → stav přepne na authenticated, komponenta zmizí (loading necháme true)
    } catch (err) {
      setError(extractErrorMessage(err, t('login.error')));
      setLoading(false);
    }
  };

  const showFooter = version.length > 0 || environment.length > 0;

  return (
    <div
      className="phx-root bg-phx-bg flex h-screen items-center justify-center p-4"
      style={GRID_BG}
    >
      <form
        onSubmit={handleSubmit}
        className="border-phx-border bg-phx-surface rounded-phx-lg shadow-phx-lg relative w-[332px] border px-[26px] pt-[26px] pb-[18px]"
        aria-label={t('login.formLabel')}
        noValidate
      >
        <div className="absolute top-2 right-2">
          <LanguageSwitcher align="end" />
        </div>

        <div className="mb-5 flex flex-col items-center gap-3">
          <div className="text-phx-text text-[20px] font-bold tracking-[-0.015em]">
            Magic<span className="text-phx-primary">3G</span>
          </div>
          <div className="text-phx-text-muted text-phx-sm -mt-1.5">{t('login.subtitle')}</div>
        </div>

        {error ? (
          <div
            role="alert"
            className="border-phx-danger/30 bg-phx-danger-tint text-phx-danger-text rounded-phx text-phx-sm mb-4 flex items-start gap-2 border px-2.5 py-2 leading-snug"
          >
            <Icon name="alert" size={14} className="text-phx-danger mt-px shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className="gap-phx-2_5 flex flex-col">
            <label
              htmlFor="login-username"
              className="text-phx-text-2 text-phx-xs tracking-phx-wide flex items-center gap-1 font-semibold uppercase"
            >
              {t('login.userLabel')} <span className="text-phx-required">*</span>
            </label>
            <Input
              id="login-username"
              name="username"
              size="xl"
              autoComplete="username"
              placeholder={t('login.userPlaceholder')}
              leadingIcon="user"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              disabled={loading}
              invalid={error != null}
              autoFocus
            />
          </div>

          <div className="gap-phx-2_5 flex flex-col">
            <label
              htmlFor="login-password"
              className="text-phx-text-2 text-phx-xs tracking-phx-wide flex items-center gap-1 font-semibold uppercase"
            >
              {t('login.passwordLabel')} <span className="text-phx-required">*</span>
            </label>
            <div className="relative">
              <Input
                id="login-password"
                name="password"
                size="xl"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                leadingIcon="lock"
                className="pr-[36px]"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                invalid={error != null}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                tabIndex={-1}
                className="text-phx-text-muted hover:bg-phx-surface-2 hover:text-phx-text-2 rounded-phx h-phx-control w-phx-control absolute top-1/2 right-1.5 flex -translate-y-1/2 items-center justify-center transition-colors"
              >
                <Icon name={showPassword ? 'eye-off' : 'eye'} size={15} />
              </button>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="xl"
          loading={loading}
          disabled={!userName || !password}
          className="mt-[18px] w-full font-semibold"
        >
          {t('login.submit')}
        </Button>

        {showFooter ? (
          <div className="border-phx-border mt-[18px] flex items-center justify-between border-t pt-3">
            <span className="text-phx-text-faint text-phx-xs font-mono">
              {version ? `v${version}` : ''}
            </span>
            {environment ? <EnvBadge env={environment} /> : <span />}
          </div>
        ) : null}
      </form>
    </div>
  );
}
